import { Action } from '@/store/actionsSlice';
import { convertJSONToLegacyFormat } from '@/store/chatInputSlice';
import { getSupabaseClient } from '@/utils/supabase';
import { StateCreator } from 'zustand';
import { CedarStore } from './types';
import { BasicStateValue } from '@/store/userStateSlice';

export interface ChatResponse {
	messages: Message[];
}

// Base properties that all messages share
type BaseMessage = {
	id?: string;
	role: MessageRole;
	text: MessageText;
	suggestions?: ChatSuggestion[];
};

export type MessageText = MessageTextTypes[] | string;

type MessageTextTypes = string | ActionText;

export type ActionText = {
	type: 'action';
	// TODO: rename this to actions
	action: Action[];
	text: string;
	icon?: string;
};

// Message types
export type MessageRole = 'bot' | 'user';

// Type for input messages where ID is optional - will be auto-generated if not provided
export type MessageInput =
	| (Omit<TextMessage, 'id'> & { id?: string })
	| (Omit<StorylineMessage, 'id'> & { id?: string });

export type Message = TextMessage | StorylineMessage;

// Message that contains text content
export type TextMessage = BaseMessage & {
	type: 'text';
};

export type StorylineMessage = BaseMessage & {
	type: 'storyline';
	sections: StorylineSection[];
};

export type StorylineSection =
	| {
			type: 'storyline_section';
			title: string;
			icon?: string;
			description: string;
	  }
	| string;

// Role colors for highlighting
export const ROLE_COLORS: Record<string, string> = {
	Marketing: 'rgba(255, 191, 233, 0.3)', // Pink
	Sales: 'rgba(191, 255, 233, 0.3)', // Green
	Engineering: 'rgba(233, 191, 255, 0.3)', // Purple
	Other: 'rgba(255, 233, 191, 0.3)', // Yellow
	default: 'rgba(255, 255, 255, 0.2)', // Default white
};

// Suggestions
export type BaseSuggestion = {
	type: string;
	icon?: string;
	onClick?: () => void;
};

export interface InputSuggestion extends BaseSuggestion {
	type: 'input_suggestion';
	input: string;
}

export interface QuestionSuggestion extends BaseSuggestion {
	type: 'question_suggestion';
	input: ChatInput[] | string;
}

// Multiple choice input type
export type ChatInput = string | MultipleChoiceSuggestion;

export interface ActionSuggestion extends BaseSuggestion {
	type: 'action_suggestion';
	label: string;
	action: Action[];
}

export interface MultipleChoiceSuggestion extends BaseSuggestion {
	type: 'multiple_choice_suggestion';
	field: string;
	fieldType: 'userContext' | 'conversationContext';
	options: string[];
	chosenValue?: string;
}

export interface StateSuggestion extends BaseSuggestion {
	type: 'state_suggestion';
	stateKey: string;
	setterName: string;
	value: BasicStateValue;
	label?: string;
}

export interface ToolCallSuggestion extends BaseSuggestion {
	type: 'tool_call_suggestion';
	toolId: string;
	label: string;
	input?: Record<string, unknown>;
}

export type ChatSuggestion =
	| ActionSuggestion
	| InputSuggestion
	| MultipleChoiceSuggestion
	| QuestionSuggestion
	| StateSuggestion
	| ToolCallSuggestion;

// Default empty array of suggestions
export const defaultSuggestions: ChatSuggestion[] = [
	{
		type: 'question_suggestion',
		icon: 'question',
		input: 'How does Gumloop work?',
	},
	{
		type: 'question_suggestion',
		icon: 'question',
		input: [
			'How can Gumloop help',
			{
				type: 'multiple_choice_suggestion',
				field: 'role',
				fieldType: 'userContext',
				options: ['Marketing', 'Sales', 'Engineering', 'Customer Support'],
			},
			'?',
		],
	},
];

// Define the messages slice
export interface MessagesSlice {
	// State
	messages: Message[];
	isProcessing: boolean;
	unreadMessages: number;
	currentThreadId: string | null;
	suggestions: ChatSuggestion[] | null;
	suggestionStack: ChatSuggestion[];
	isBarActive: boolean;

	// Actions
	setMessages: (messages: Message[]) => void;
	addMessage: (message: MessageInput) => Message;
	clearMessages: () => void;
	setIsProcessing: (isProcessing: boolean) => void;
	setUnreadMessages: (count: number) => void;
	incrementUnreadMessages: () => void;
	resetUnreadMessages: () => void;
	nextMessage: (userInput: string | TextMessage) => Promise<void>;
	deleteChats: (threadId?: string) => Promise<void>;
	createThread: (threadId: string, title?: string) => Promise<void>;
	initializeChat: () => Promise<ChatResponse>;
	setSuggestions: (suggestions: ChatSuggestion[]) => void;
	clickSuggestion: (
		suggestions: ChatSuggestion[],
		suggestionIndex: number
	) => void;
	setIsBarActive: (isBarActive: boolean) => void;
}

// Create the messages slice
export const createMessagesSlice: StateCreator<
	CedarStore,
	[],
	[],
	MessagesSlice
> = (set, get) => {
	return {
		// Default state
		messages: [],
		isProcessing: false,
		unreadMessages: 0,
		currentThreadId: null,
		suggestions: null,
		suggestionStack: [],
		isBarActive: false,

		// Actions
		setMessages: (messages: Message[]) => set({ messages }),

		setIsBarActive: (isBarActive: boolean) => set({ isBarActive }),

		addMessage: (messageData: MessageInput): Message => {
			const newMessage = {
				...messageData,
				id:
					messageData.id ||
					`message-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
				suggestions: messageData.suggestions || [],
			} as Message;

			set((state) => ({
				messages: [...state.messages, newMessage],
				unreadMessages:
					messageData.role === 'bot'
						? state.unreadMessages + 1
						: state.unreadMessages,
				suggestions:
					messageData.type === 'storyline'
						? messageData.suggestions || []
						: state.suggestions,
			}));

			return newMessage;
		},

		clearMessages: () =>
			set({
				messages: [],
				suggestions: defaultSuggestions,
				suggestionStack: [],
			}),

		setIsProcessing: (isProcessing: boolean) => set({ isProcessing }),

		setUnreadMessages: (unreadMessages: number) => set({ unreadMessages }),

		incrementUnreadMessages: () =>
			set((state) => ({ unreadMessages: state.unreadMessages + 1 })),

		resetUnreadMessages: () => set({ unreadMessages: 0 }),

		// Process user message and generate response
		nextMessage: async (userInput: string | TextMessage) => {
			const state = get();

			// Handle both string input and MessageInput object
			let userMessage: string;
			let userMessageObj: TextMessage;

			if (typeof userInput === 'string') {
				userMessage = userInput;
				userMessageObj = {
					role: 'user',
					type: 'text',
					text: userMessage,
				};
			} else {
				userMessageObj = userInput;
				// Ensure the message object has required fields
				if (userMessageObj.type === 'text') {
					userMessage =
						typeof userMessageObj.text === 'string'
							? userMessageObj.text
							: Array.isArray(userMessageObj.text)
								? userMessageObj.text
										.map((t) => (typeof t === 'string' ? t : t.text))
										.join(' ')
								: '';
				} else {
					// For non-text types, we can't proceed
					console.error('nextMessage only supports text messages');
					return;
				}
			}

			if (!userMessage.trim() || state.isProcessing) return;

			if (userMessageObj.text === 'How does Gumloop work?') {
				const storylineMessage: StorylineMessage = {
					role: 'bot',
					type: 'storyline',
					text: 'Gumloop makes it simple for you to automate work you spend hours on. How?',
					sections: [
						{
							type: 'storyline_section',
							title: 'Inputs',
							icon: '📥',
							description:
								'Gumloop breaks down complex tasks into modular pieces you can work with. For example, you can listen to every post that mentions a topic online with a single block.',
						},
						{
							type: 'storyline_section',
							title: 'Processing',
							icon: '⚙️',
							description:
								"Now we've gotten some data to work with, Gumloop provides tons of ways to manipulate it. For example, we can take any data and categorise it into severity, such as 'High', 'Medium', 'Low'.",
						},
						{
							type: 'storyline_section',
							title: 'Outputs',
							icon: '📤',
							description:
								'Gumloop can output data to a spreadsheet, Slack, or your email inbox, so you can use it in your existing workflows.',
						},
						"Best part? You get to mix and match these nodes to create a custom workflow that works for you. I know this is a lot, but I'm happy to generate flows for you!",
					],
				};
				state.addMessage(storylineMessage);
				// New set of suggestions
				state.setSuggestions(defaultSuggestions);
				return;
			}

			set({ isProcessing: true });

			// Add user message
			state.addMessage(userMessageObj);

			// Also remove input_suggestions from the suggestions array
			const filteredSuggestions = state.suggestions?.filter(
				(suggestion) => suggestion.type !== 'input_suggestion'
			);
			set({ suggestions: filteredSuggestions || [] });

			// Remove all input_suggestion and action_suggestion suggestions from the suggestionsStack
			set((state) => {
				const filteredSuggestionStack = state.suggestionStack.filter(
					(suggestion) => suggestion.type !== 'input_suggestion'
				);

				return { suggestionStack: filteredSuggestionStack };
			});

			try {
				// Ensure we have a thread ID
				if (!state.currentThreadId) {
					if (!state.userId) throw new Error('No user ID found');
					await state.initializeChat();
				}

				if (!state.currentThreadId) {
					throw new Error('Failed to initialize chat thread');
				}

				const botMessage = state.addMessage({
					role: 'bot',
					type: 'text',
					text: '',
				});

				let streamedSuggestions: ChatSuggestion[] = [];

				await get().sendMessageStream(userMessage, {
					onMessage: (chunk) => {
						set((s) => {
							const msgIndex = s.messages.findIndex(
								(m) => m.id === botMessage.id
							);
							if (msgIndex === -1) {
								return {};
							}
							const msg = s.messages[msgIndex];
							const currentText = typeof msg.text === 'string' ? msg.text : '';
							const updatedMsg = { ...msg, text: currentText + chunk };
							const updatedMessageWithNewlines = updatedMsg.text.replace(
								/(\\n|\n)/g,
								'\n'
							);
							const messages = [...s.messages];
							messages[msgIndex] = {
								...updatedMsg,
								text: updatedMessageWithNewlines,
							};
							return { messages };
						});
					},
					onSuggestions: (sugs) => {
						streamedSuggestions = sugs;
					},
					onDone: async () => {
						get().setSuggestions(streamedSuggestions);
						const supabase = getSupabaseClient();
						await supabase
							.from('chat_threads')
							.update({ last_message_at: new Date().toISOString() })
							.eq('thread_id', get().currentThreadId as string);
						set({ isProcessing: false });
					},
				});
			} catch (error) {
				console.error('Error processing message:', error);

				const errorMessageObj: TextMessage = {
					role: 'bot',
					type: 'text',
					text: "I'm sorry, I encountered an error. Please try again later.",
				};

				get().addMessage(errorMessageObj);
				set({ isProcessing: false });
			}
		},

		deleteChats: async (threadId?: string) => {
			try {
				const { userId } = get();
				const supabase = getSupabaseClient();

				if (!userId) throw new Error('No user ID found');

				if (threadId) {
					// First call the backend to delete the specific thread
					await get().deleteChatThread(threadId);

					// Soft delete specific thread
					const { error } = await supabase
						.from('chat_threads')
						.update({ deleted_at: new Date().toISOString() })
						.eq('thread_id', threadId)
						.eq('product_user_id', userId);

					if (error) throw error;
				} else {
					// Get all active threads for the user
					const { data: threads, error: queryError } = await supabase
						.from('chat_threads')
						.select('thread_id')
						.eq('product_user_id', userId)
						.is('deleted_at', null);

					if (queryError) throw queryError;

					// Delete each thread from the backend
					if (threads) {
						for (const thread of threads) {
							await get().deleteChatThread(thread.thread_id);
						}
					}

					// Soft delete all threads for user
					const { error } = await supabase
						.from('chat_threads')
						.update({ deleted_at: new Date().toISOString() })
						.eq('product_user_id', userId);

					if (error) throw error;
				}

				// Clear local messages if we're deleting the current thread or all threads
				if (!threadId || threadId === get().currentThreadId) {
					set({ messages: [] });
				}
			} catch (error) {
				console.error('Error deleting chats:', error);
				throw error;
			}
		},

		createThread: async (threadId: string, title?: string): Promise<void> => {
			try {
				const { validateOrCreateProductUser, userId } = get();

				if (!userId) {
					throw new Error('No user ID');
				}

				const supabase = getSupabaseClient();

				await validateOrCreateProductUser();

				const { error } = await supabase.from('chat_threads').insert({
					product_user_id: userId,
					thread_id: threadId,
					title: title || null,
					created_at: new Date().toISOString(),
					last_message_at: new Date().toISOString(),
				});

				if (error) throw error;
			} catch (error) {
				console.error('Error creating thread:', error);
				throw error;
			}
		},

		initializeChat: async (): Promise<ChatResponse> => {
			try {
				const { productId } = get();
				const userId = get().userId;
				if (!userId || !productId) {
					throw new Error('User ID or Product ID not found');
				}

				const supabase = getSupabaseClient();

				// Query for existing threads
				const { data: existingThreads, error: queryError } = await supabase
					.from('chat_threads')
					.select('*')
					.eq('product_user_id', userId)
					.is('deleted_at', null)
					.order('last_message_at', { ascending: false })
					.limit(1);

				if (queryError) throw queryError;

				let chatResponse: ChatResponse;

				if (existingThreads && existingThreads.length > 0) {
					// Use the most recent thread
					const thread = existingThreads[0];
					set({ currentThreadId: thread.thread_id });

					// Initialize chat with the existing thread
					chatResponse = await get().initializeChatBackend(thread.thread_id);
					set({ messages: chatResponse.messages });
				} else {
					// Create a new thread
					const newThreadId = `thread-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
					await get().createThread(newThreadId);
					set({ currentThreadId: newThreadId });

					// Initialize chat with the new thread
					chatResponse = await get().initializeChatBackend(newThreadId);
					set({ messages: chatResponse.messages });
				}

				return chatResponse;
			} catch (error) {
				console.error('Error initializing chat:', error);
				throw error;
			}
		},

		setSuggestions: (suggestions: ChatSuggestion[]) => {
			set((state) => {
				// Create a new suggestion stack by copying the existing one
				const newSuggestionStack = [...state.suggestionStack];

				// Find the difference between previous and current suggestions
				if (state.suggestions && state.suggestions.length > 0) {
					// Create a list of removed suggestions from the previous suggestions
					const removedSuggestions = state.suggestions.filter(
						(prevSuggestion) =>
							!suggestions.some(
								(newSuggestion) =>
									JSON.stringify(prevSuggestion) ===
									JSON.stringify(newSuggestion)
							)
					);

					// Filter for only question_suggestion and input_suggestion types
					const suggestionsToStack = removedSuggestions.filter(
						(suggestion) =>
							suggestion.type === 'question_suggestion' ||
							suggestion.type === 'action_suggestion' ||
							suggestion.type === 'input_suggestion'
					);

					// Add suggestions one by one to maintain order
					if (suggestionsToStack.length > 0) {
						// Add all suggestions in reverse order by popping them one by one
						while (suggestionsToStack.length > 0) {
							const suggestion = suggestionsToStack.pop();
							if (suggestion) {
								newSuggestionStack.push(suggestion);
							}
						}
					}
				}

				// Prepare final suggestions, starting with the provided new suggestions
				const finalSuggestions = [...suggestions];

				// Add if finalSuggestions doesn't include a multiple_choice_suggestion
				const hasMCSuggestion = finalSuggestions.some(
					(suggestion) => suggestion.type === 'multiple_choice_suggestion'
				);

				if (!hasMCSuggestion) {
					// Calculate how many suggestions we need
					const suggestionsNeeded = Math.min(
						3 - finalSuggestions.length,
						newSuggestionStack.length
					);

					// Add suggestions one by one from the stack to maintain order
					if (suggestionsNeeded > 0) {
						// Take the needed suggestions from the END of the stack (most recent first)
						const suggestionsToAdd = [];
						for (let i = 0; i < suggestionsNeeded; i++) {
							if (newSuggestionStack.length > 0) {
								// Get the most recent suggestion (from the end)
								const suggestion = newSuggestionStack.pop();
								if (suggestion) {
									suggestionsToAdd.push(suggestion);
								}
							}
						}

						// Add each suggestion if it's not a duplicate
						for (const previousSuggestion of suggestionsToAdd) {
							if (
								!finalSuggestions.some(
									(existingSuggestion) =>
										JSON.stringify(existingSuggestion) ===
										JSON.stringify(previousSuggestion)
								)
							) {
								finalSuggestions.push(previousSuggestion);
							}
						}
					}
				}

				return {
					suggestions: finalSuggestions,
					suggestionStack: newSuggestionStack,
				};
			});
		},

		clickSuggestion: (
			suggestions: ChatSuggestion[],
			suggestionIndex: number
		) => {
			const { setOverrideInputContent, setIsBarActive } = get();

			// Set isBarActive to true
			setIsBarActive(true);

			// Iterate through suggestions and their options to find the correct item at the given index
			let counter = 1;
			let selectedSuggestion: ChatSuggestion | null = null;
			let actualSuggestionIndex = 0;
			let selectedOption: string | null = null;

			// Use a simple for loop with counter as requested
			for (let i = 0; i < suggestions.length; i++) {
				const suggestion = suggestions[i];

				if (suggestion.type === 'multiple_choice_suggestion') {
					const mcSuggestion = suggestion as MultipleChoiceSuggestion;

					// For multiple choice, we need to count each option
					for (let j = 0; j < mcSuggestion.options.length; j++) {
						// Skip the chosen value if it exists
						if (mcSuggestion.options[j] !== mcSuggestion.chosenValue) {
							if (counter === suggestionIndex) {
								selectedSuggestion = mcSuggestion;
								actualSuggestionIndex = i;
								selectedOption = mcSuggestion.options[j];
								break;
							}
							counter++;
						}
					}
				} else {
					// For other suggestion types, count the suggestion itself
					if (counter === suggestionIndex) {
						selectedSuggestion = suggestion;
						actualSuggestionIndex = i;
						break;
					}
					counter++;
				}

				// If we've already found what we need, break out of the loop
				if (selectedSuggestion) break;
			}

			// If no suggestion was found at the given index, return
			if (!selectedSuggestion) return;

			// Handle different suggestion types with a switch statement
			switch (selectedSuggestion.type) {
				case 'question_suggestion':
					const questionSuggestion = selectedSuggestion as QuestionSuggestion;
					setOverrideInputContent(questionSuggestion.input);

					// Call the onClick handler if provided
					if (questionSuggestion.onClick) {
						questionSuggestion.onClick();
					}

					// Remove the clicked suggestion from the array
					const updatedSuggestions = suggestions.filter(
						(_, index) => index !== actualSuggestionIndex
					);

					get().setSuggestions(updatedSuggestions);
					break;

				case 'input_suggestion':
					const inputSuggestion = selectedSuggestion as InputSuggestion;
					setOverrideInputContent(inputSuggestion.input);

					// Call the onClick handler if provided
					if (inputSuggestion.onClick) {
						inputSuggestion.onClick();
					}

					// Remove the clicked suggestion from the array
					const newSuggestions = suggestions.filter(
						(_, index) => index !== actualSuggestionIndex
					);

					// Also add the removed suggestion to the stack
					set((state) => {
						const latestSuggestion =
							state.suggestionStack[state.suggestionStack.length - 1];

						const updatedStack = [
							...state.suggestionStack.slice(0, -1),
							inputSuggestion,
						];
						if (latestSuggestion?.type === 'input_suggestion') {
							return {
								suggestions: [...newSuggestions, latestSuggestion],
								suggestionStack: updatedStack,
							};
						}
						return {
							suggestions: newSuggestions,
							suggestionStack: updatedStack,
						};
					});
					break;

				case 'state_suggestion':
					const stateSuggestion = selectedSuggestion as StateSuggestion;

					// Get the relevant state from userStateSlice
					const state = get().getState(stateSuggestion.stateKey);

					if (
						state &&
						state.setters &&
						state.setters[stateSuggestion.setterName]
					) {
						// Get the setter function
						const setter = state.setters[stateSuggestion.setterName];

						// Execute the setter with the provided value
						try {
							setter.execute(stateSuggestion.value);

							// Add a message to indicate the state change was successful
							get().addMessage({
								role: 'bot',
								type: 'text',
								text: `**Completed**: ${stateSuggestion.label || 'State update for ' + stateSuggestion.stateKey}`,
							});
						} catch (error) {
							console.error(
								`Error executing setter ${stateSuggestion.setterName} for state ${stateSuggestion.stateKey}:`,
								error
							);

							// Add an error message
							get().addMessage({
								role: 'bot',
								type: 'text',
								text: `**Error**: ${stateSuggestion.label || 'State update for ' + stateSuggestion.stateKey}.`,
							});
						}
					} else {
						console.error(
							`State or setter not found: ${stateSuggestion.stateKey}.${stateSuggestion.setterName}`
						);

						// Add an error message
						get().addMessage({
							role: 'bot',
							type: 'text',
							text: `Error completing: ${stateSuggestion.label || 'State update for ' + stateSuggestion.stateKey}.`,
						});
					}

					// Remove the suggestion after execution
					const stateUpdatedSuggestions = suggestions.filter(
						(_, index) => index !== actualSuggestionIndex
					);
					get().setSuggestions(stateUpdatedSuggestions);

					break;

				case 'multiple_choice_suggestion':
					const mcSuggestion = selectedSuggestion as MultipleChoiceSuggestion;
					const fieldToUpdate = mcSuggestion.field;

					// Use the option we found during iteration
					if (selectedOption) {
						// Get the current editor content or create a new array
						const currentContent = get().chatInputContent;

						let finalInputContent: string | ChatInput[] = selectedOption;

						if (currentContent && 'content' in currentContent) {
							// Deep clone the JSON content
							const updatedContent = JSON.parse(JSON.stringify(currentContent));

							// Update the choice node in the JSON structure
							if (updatedContent.content && updatedContent.content.length > 0) {
								for (const block of updatedContent.content) {
									if (block.content && Array.isArray(block.content)) {
										for (const node of block.content) {
											if (
												node.type === 'choice' &&
												node.attrs &&
												node.attrs.field === fieldToUpdate
											) {
												// Update the selected option
												node.attrs.selectedOption = selectedOption;
											}
										}
									}
								}
							}

							// Convert to string/array format for override
							finalInputContent = convertJSONToLegacyFormat(updatedContent);
						}

						const updatedSuggestion: MultipleChoiceSuggestion = {
							...mcSuggestion,
							chosenValue: selectedOption,
						};

						// Update only the specific suggestion while preserving others
						const currentSuggestions = [...suggestions];
						currentSuggestions[actualSuggestionIndex] = updatedSuggestion;

						set({ suggestions: currentSuggestions });
						setOverrideInputContent(finalInputContent);
					}
					break;

				case 'action_suggestion':
					const actionSuggestion = selectedSuggestion as ActionSuggestion;

					// Call onClick handler if available
					if (actionSuggestion.onClick) {
						actionSuggestion.onClick();
					}

					// Handle any action execution if needed
					if (actionSuggestion.action && actionSuggestion.action.length > 0) {
						get().addActionsToStart(actionSuggestion.action);
					}

					// Remove the clicked suggestion from the array
					const updatedActionSuggestions = suggestions.filter(
						(_, index) => index !== actualSuggestionIndex
					);

					// Update suggestions
					if (updatedActionSuggestions.length > 0) {
						get().setSuggestions(updatedActionSuggestions);
					}
					break;

				case 'tool_call_suggestion':
					const toolSuggestion = selectedSuggestion as ToolCallSuggestion;
					// Call onClick handler if provided
					if (toolSuggestion.onClick) {
						toolSuggestion.onClick();
					}
					try {
						get().invokeTool(toolSuggestion.toolId, toolSuggestion.input || {});
						// Only show success message after successful invocation
						get().addMessage({
							role: 'bot',
							type: 'text',
							text: '**Completed:** ' + toolSuggestion.label,
						});
					} catch (error) {
						console.error('Error invoking tool:', error);
						get().addMessage({
							role: 'bot',
							type: 'text',
							text: '**Error:** Failed to complete ' + toolSuggestion.label,
						});
					}
					// Remove the clicked suggestion
					const updatedToolSuggestions = suggestions.filter(
						(_, index) => index !== actualSuggestionIndex
					);
					get().setSuggestions(updatedToolSuggestions);
					break;

				default:
					break;
			}
		},
	};
};
